import {
  convertDateToYYYYMMDDFormat,
  getFirstDayOfMonth,
  getToday,
  STATUSPAGE,
  SUBMIT_BUTTON,
} from '@repo/ui';
import { NumberStudentsPerProfessionalInFieldPeriodType } from 'domain/models';
import {
  GetNumberStudentsPerProfessionalInFieldUseCaseInterface,
  ExportNumberStudentsPerProfessionalInFieldUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { Option } from 'presentation/app/models/option';
import { useCallback, useRef, useState } from 'react';
import * as Yup from 'yup';

const periodTypeOptions: {
  label: string;
  value: NumberStudentsPerProfessionalInFieldPeriodType;
}[] = [
  {
    label: 'Data da Atividade',
    value: 'ACTIVITY_DATE',
  },
  {
    label: 'Data da Validacao',
    value: 'VALIDATION_DATE',
  },
];

type PeriodTypeOption = Option & {
  value: NumberStudentsPerProfessionalInFieldPeriodType;
};

type NumberStudentsPerProfessionalInFieldFormValues = {
  entity: Option | null;
  course: Option | null;
  unit: Option | null;
  semester: Option | null;
  groups: Option | null;
  periodType: PeriodTypeOption;
  starts: string;
  ends: string;
};

export const useNumberStudentsPerProfessionalInField = () => {
  // hooks
  const { getLegends } = useLegends();
  const { reportService } = useService();

  // states
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const selectedSubmitBtnRef = useRef<SUBMIT_BUTTON | null>(null);
  const [tableLoading, setTableLoading] = useState(STATUSPAGE.DONE);
  const [buttonsLoading, setButtonsLoading] = useState(STATUSPAGE.DONE);
  const [
    numberStudentsPerProfessionalInFieldData,
    setNumberStudentsPerProfessionalInFieldData,
  ] = useState<GetNumberStudentsPerProfessionalInFieldUseCaseInterface.Result>({
    dates: [],
    data: [],
  });

  const validation = useFormik<NumberStudentsPerProfessionalInFieldFormValues>({
    enableReinitialize: true,

    initialValues: {
      entity: null,
      course: null,
      unit: null,
      semester: null,
      groups: null,
      periodType: periodTypeOptions[0],
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
      ends: convertDateToYYYYMMDDFormat(getToday()),
    },

    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      periodType: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string().oneOf(['ACTIVITY_DATE', 'VALIDATION_DATE']),
        })
        .nullable()
        .required('Tipo de periodo e obrigatorio'),
      starts: Yup.date().nullable().required('Data inicial e obrigatorio'),
      ends: Yup.date()
        .nullable()
        .min(Yup.ref('starts'), 'Data final deve ser maior que a inicial')
        .required('Data final e obrigatorio'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        periodType: values.periodType.value,
        startDate: values.starts,
        endDate: values.ends,
        entityHashId: values.entity?.value,
        courseHashId: values.course?.value,
        semesterHashId: values.semester?.value,
        curricularUnitHashId: values.unit?.value,
        groupHashId: values.groups?.value,
      };

      switch (selectedSubmitBtnRef.current) {
        case SUBMIT_BUTTON.EXCEL:
          requestExportNumberStudentsPerProfessionalInField(valuesToSubmit);
          break;
        case SUBMIT_BUTTON.JSON:
          requestGetNumberStudentsPerProfessionalInFieldJSON(valuesToSubmit);
          break;
        default:
          break;
      }
    },
  });

  const requestExportNumberStudentsPerProfessionalInField = useCallback(
    async (
      params: ExportNumberStudentsPerProfessionalInFieldUseCaseInterface.Params,
    ) => {
      setButtonsLoading(STATUSPAGE.LOADING);
      await reportService
        .exportNumberStudentsPerProfessionalInField(params)
        .then((res) => {
          const blob = new Blob([res], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = 'estudantes-por-profissional-em-campo.xlsx';
          link.click();
        })
        .catch((err) => notifyError(err.message))
        .finally(() => setButtonsLoading(STATUSPAGE.DONE));
    },
    [],
  );

  const requestGetNumberStudentsPerProfessionalInFieldJSON = useCallback(
    async (
      params: GetNumberStudentsPerProfessionalInFieldUseCaseInterface.Params,
    ) => {
      setTableLoading(STATUSPAGE.LOADING);
      setButtonsLoading(STATUSPAGE.LOADING);
      await reportService
        .getNumberStudentsPerProfessionalInField(params)
        .then((res) => setNumberStudentsPerProfessionalInFieldData(res))
        .catch((err) => {
          setNumberStudentsPerProfessionalInFieldData({
            dates: [],
            data: [],
          });
          notifyError(err.message);
        })
        .finally(() => {
          setTableLoading(STATUSPAGE.DONE);
          setButtonsLoading(STATUSPAGE.DONE);
          setHasFetchedData(true);
        });
    },
    [],
  );

  return {
    validation,
    tableLoading,
    buttonsLoading,
    hasFetchedData,
    selectedSubmitBtnRef,
    numberStudentsPerProfessionalInFieldData,
    periodTypeOptions,
    getLegends,
  };
};
